import React, { PureComponent, Fragment } from 'react';
import { object, array, oneOfType, func, number } from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment';
import isEqual from 'lodash.isequal';
import {
  ActionList,
  Button,
  Card,
  ChartEmptyState,
  Databoxes,
  LineChart,
} from 'common/components';
import { SelectComponent } from 'common/components/Select';
import { getMonthNameFromNumber, getMonthNumberFromName } from 'common/utils';
import { toCommaNumber } from 'common/formatters';
import { base64_decode } from 'util/base64';
import { ROUTE_FIELDS } from 'common/constants';
import { YieldsForm } from 'containers';

const currentMonthNum = Number(moment().format('M'));
const emptyStateText = {
  chartTitle: 'Yield History',
  title: 'Yield history not available.',
  subTitle: 'Add the expected yield to the crops.',
  buttonText: 'Add Yield',
};

/**
 * Form Column Header: Update accordingly when renderFields() column changes
 */
const columnHeader = [
  {
    content: (
      <Fragment>
        <div className="column">Field</div>
        <div className="column">Expected Yield (bu/acre)</div>
      </Fragment>
    ),
  },
];

/**
 *
 *
 * @export
 * @class YieldChart
 * @extends {PureComponent}
 */
export class YieldChart extends PureComponent {
  static displayName = 'YieldChart';

  static propTypes = {
    browser: object,
    data: oneOfType([object, array]), // TODO: define prop properly
    goToFieldPage: func,
    season: number,
    refetch: func.isRequired,
  };

  static defaultProps = {
    data: [],
  };

  state = initialState(this.props);

  constructor(props) {
    super(props);
    this.chartContainer = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const element = this.chartContainer.current;
    if (element) {
      this.setState({
        chartContainerWidth: element.getBoundingClientRect().width,
      });
    }
  };

  /**
   * @function renderDropdown
   * @memberof LineChart
   * @returns {React.Dom} - <SelectComponent /> crop list dropdown
   */
  renderDropdown = () => {
    const { selectOptions } = this.state;
    return (
      <SelectComponent
        disablePromptOption
        name="crop"
        title=""
        options={selectOptions}
        input={{ onChange: event => this._handleCropSelectChange(event) }}
        meta={{ touched: false, error: undefined }}
      />
    );
  };

  /**
   * @function renderList
   * @memberof LineChart
   * @returns {React.Dom} - <ActionList /> list of fields
   */
  renderList = key => {
    const { listData } = this.state;
    if (!listData) {
      return null;
    }

    return {
      content: (
        <Fragment key={key}>
          <div className="column">{listData[key].fieldName}</div>
          <div className="column">{`${toCommaNumber(
            listData[key].yield
          )} bu`}</div>
        </Fragment>
      ),
    };
  };

  /**
   * @function renderChartView
   * @memberof LineChart
   * @returns {React.Dom} - <LineChart /> View
   */
  renderChartView = (width, height, maxHeight) => {
    const {
      chartData: { fullYearData, latestDataPoint },
      listData,
    } = this.state;
    const latestExpected = Object.keys(latestDataPoint).reduce(
      (sum, current) => sum + Number(latestDataPoint[current].harvest),
      0
    );

    return (
      <Card
        header={
          <h2 className="text--header text--medium">{'Expected Harvest'}</h2>
        }
        headerright={this.renderDropdown()}
        theme={['noborder']}
      >
        <div className="columns__row">
          <div className="is-flex-widescreen">
            <div
              className="column is-two-thirds-widescreen"
              ref={this.chartContainer}
            >
              <Databoxes
                data={[
                  {
                    label: 'Total expected harvest',
                    line: {
                      output: toCommaNumber(Math.round(latestExpected)),
                      unit: 'bu',
                    },
                  },
                ]}
                theme={['overwrap']}
              />
              <LineChart
                chartData={fullYearData}
                width={width}
                height={height}
                unit="Bushels"
              />
            </div>
            <div className="column is-one-third-widescreen">
              <ActionList
                theme={['columns', 'columns__header']}
                cards={columnHeader}
                flexItem
              />
              <ActionList
                theme={['columns', 'bordered']}
                cards={Object.keys(listData).map(this.renderList)}
                style={{ height: maxHeight }}
                flexItem
              />

              <footer className="field__group text--right">
                <Button
                  type="button"
                  className="btn"
                  onClick={this._toggelYieldChart}
                >
                  Update Expected Yield
                </Button>
              </footer>
            </div>
          </div>
        </div>
      </Card>
    );
  };

  /**
   * @function renderFormView
   * @memberof LineChart
   * @returns {React.Dom} - <YieldsForm /> View
   */
  renderFormView = height => {
    const { listData } = this.state;
    const { refetch } = this.props;
    const crops = Object.keys(listData).map(key => listData[key]);

    let initialValues = {};
    Object.keys(listData).forEach(key => {
      initialValues[listData[key].cropId] = listData[key].yield;
    });

    return (
      <Card
        header={
          <h2 className="text--header text--medium">{'Expected Yield'}</h2>
        }
        headerright={this.renderDropdown()}
        theme={['noborder']}
      >
        <YieldsForm
          crops={crops}
          initialValues={initialValues}
          closeForm={this._toggelYieldChart}
          refetch={refetch}
          style={{ height }}
        />
      </Card>
    );
  };

  /**
   * When the data is updated, manually trigger _handleCropSelectChange()
   */
  componentDidUpdate(prevProps) {
    if (isEqual(prevProps.data, this.props.data)) {
      return;
    }

    const { selectedOption } = this.state;
    this._handleCropSelectChange({ target: { value: selectedOption } });
  }

  render() {
    const {
      chartContainerWidth,
      chartData: { fullYearData },
    } = this.state;
    const { browser, goToFieldPage } = this.props;

    // If there is no data, show empty state
    if (!fullYearData || !fullYearData.length)
      return <ChartEmptyState {...emptyStateText} navigateTo={goToFieldPage} />;

    const height = browser.greaterThan.mobile ? 400 : 350;
    const maxHeight = height - (64 + 48); // 64 (column header height) + 48 (button height)

    if (!this.state.showYieldChart) {
      return this.renderFormView(maxHeight);
    }

    let width;
    if (browser.is.fullhd) {
      width = 629;
    } else if (browser.is.widescreen) {
      width = 533;
    } else if (browser.is.desktop) {
      width = 427;
    } else if (browser.is.tablet) {
      width = chartContainerWidth;
    } else {
      width = Math.max(600, chartContainerWidth);
    }

    return this.renderChartView(width, height, maxHeight);
  }

  /**
   *
   *
   * @function _handleCropSelectChange
   * @memberof LineChart
   * @param {Event}
   */
  _handleCropSelectChange = ({ target: { value } }) => {
    const { data, season } = this.props;
    const selectedOption = value;
    const dataPoints = (data[value] || {}).dataPoints;
    const chartData = fullYearData(dataPoints, season);

    this.setState({
      selectedOption,
      chartData,
      listData: getCategoryData(chartData),
    });
  };

  /**
   *
   * @function _toggelYieldChart
   * @memberof Dashboard
   */
  _toggelYieldChart = () => {
    this.setState({ showYieldChart: !this.state.showYieldChart });
  };
}

const mapState = ({ browser, settings: { season } }) => ({ browser, season });
const mapDispatchToProps = {
  goToFieldPage: () => push(ROUTE_FIELDS),
};

export default connect(
  mapState,
  mapDispatchToProps
)(YieldChart);

/**
 *
 * @function getCategoryData
 * @param {Object} - processed dataPoints data
 * @returns {Object}
 */
const getCategoryData = ({ latestDataPoint }) => {
  let listData = {};
  if (!latestDataPoint) {
    return listData;
  }

  Object.keys(latestDataPoint).forEach(key => {
    listData[key] = {
      ...latestDataPoint[key],
      cropId: latestDataPoint[key].cropId,
      fieldName: base64_decode(key),
    };
  });

  return listData;
};

/**
 *
 * @function initialState
 * @param {Object} - data from graphql
 * @returns {Object}
 */
const initialState = ({ data, season }) => {
  let initialState = {
    chartContainerWidth: 550,
    selectOptions: [],
    selectedOption: null,
    chartData: {},
    listData: {},
    showYieldChart: true,
  };

  const chartData = Object.keys(data).filter(key => key !== 'selectOptions');

  if (chartData.length) {
    const selectedOption = data.selectOptions[0].value;
    const dataPoints = data[selectedOption].dataPoints;
    const chartData = fullYearData(dataPoints, season);

    initialState = {
      ...initialState,
      selectOptions: data.selectOptions,
      selectedOption,
      chartData,
      listData: getCategoryData(chartData),
    };
  }
  return initialState;
};

/**
 *
 * @function fullYearData
 * @param {Object} chartData - aggregated chart data
 * @returns {Object}
 */
const fullYearData = (chartData, season) => {
  chartData = chartData || [];

  /**
   * Get latest data point in the set
   */
  let globalLatest = null;
  let seasonLatest = null;

  /**
   * Iterate through the full 12 months and add fake data where needed
   */
  let fullYearData = [];
  let latestSeason = {};
  fullYear.forEach((item, index) => {
    /**
     * Currently month in the full 12 months array
     */
    const monthName = getMonthNameFromNumber(index);

    /**
     * Does chartData have data for the current iterated month (from fullYear) ?
     * if so, append matched month value
     */
    const monthInSet = chartData.find(({ x }) => x === monthName);
    if (monthInSet) {
      let globalPoint = {};
      let seasonPoint = {};

      Object.keys(monthInSet.y).forEach(key => {
        const y = monthInSet.y[key];
        // get latest of the field
        if (!latestSeason[key] || y.season >= latestSeason[key]) {
          globalPoint[key] = y;
          latestSeason[key] = y.season;
        }
        if (y.season === season) {
          seasonPoint[key] = y;
        }
      });
      globalLatest = { ...globalLatest, ...globalPoint };
      seasonLatest = { ...seasonLatest, ...seasonPoint };
    }

    /**
     * sum each data point to get total for the month
     */
    const isPast =
      season < moment().year() ||
      (season === moment().year() && index < currentMonthNum);
    const y =
      seasonLatest && isPast
        ? Object.keys(seasonLatest).reduce((sum, curr) => {
            // add only when current data matches with the selected season
            return sum + Number(seasonLatest[curr].harvest);
          }, 0)
        : null;

    fullYearData = [...fullYearData, { x: monthName, y }];
  });

  /**
   * Sort data after adding filler months
   */
  fullYearData.sort((a, b) => {
    const aMonthNumber = getMonthNumberFromName(a.x);
    const bMonthNumber = getMonthNumberFromName(b.x);
    return aMonthNumber - bMonthNumber;
  });

  return { fullYearData, latestDataPoint: globalLatest };
};

/**
 * Used by fullYearData() to generate a full 12 months of chart data
 */
const fullYear = [
  { x: getMonthNameFromNumber(0), y: null },
  { x: getMonthNameFromNumber(1), y: null },
  { x: getMonthNameFromNumber(2), y: null },
  { x: getMonthNameFromNumber(3), y: null },
  { x: getMonthNameFromNumber(4), y: null },
  { x: getMonthNameFromNumber(5), y: null },
  { x: getMonthNameFromNumber(6), y: null },
  { x: getMonthNameFromNumber(7), y: null },
  { x: getMonthNameFromNumber(8), y: null },
  { x: getMonthNameFromNumber(9), y: null },
  { x: getMonthNameFromNumber(10), y: null },
  { x: getMonthNameFromNumber(11), y: null },
];



// WEBPACK FOOTER //
// ./src/containers/YieldChart/index.js